// Notification permission prompt + on/off toggle for the map screen.
// Non-intrusive: never on first load, never again after "Not now".

import React from "react";
import { Bell, BellOff, X, Loader2, ExternalLink } from "lucide-react";
import { toast } from "sonner";
import {
  dismissNotice,
  inAppBrowserName,
  noticeDismissed,
  openInExternalBrowser,
} from "../lib/inAppBrowser";
import {
  bumpSession,
  dismissPrompt,
  disablePush,
  enablePush,
  getSubscription,
  permissionState,
  pushSupported,
  shouldPrompt,
} from "../lib/push";

export default function NotificationsPill({ variant = "pill" }) {
  const row = variant === "row";
  const [mode, setMode] = React.useState("hidden"); // hidden | inapp | prompt | on | off
  const [busy, setBusy] = React.useState(false);

  React.useEffect(() => {
    let alive = true;
    bumpSession();
    (async () => {
      if (!pushSupported()) {
        // In-app WebViews (Instagram, Facebook…) cannot do Web Push at all.
        if (inAppBrowserName() && !noticeDismissed()) setMode("inapp");
        return;
      }
      const sub = await getSubscription();
      if (!alive) return;
      if (sub && permissionState() === "granted") setMode("on");
      else if (permissionState() === "granted") setMode("off");
      else if (shouldPrompt()) setMode("prompt");
    })();
    return () => {
      alive = false;
    };
  }, []);

  const turnOn = async () => {
    setBusy(true);
    try {
      await enablePush();
      setMode("on");
      toast.success("Daily reminder on. We'll nudge you once a day.");
    } catch (err) {
      if (err?.message === "denied")
        toast.error("Notifications are blocked. You can allow them in your browser settings.");
      else toast.error("Could not turn on notifications.");
      setMode(permissionState() === "default" ? "prompt" : "off");
    } finally {
      setBusy(false);
    }
  };

  const turnOff = async () => {
    setBusy(true);
    try {
      await disablePush();
      setMode("off");
      toast.message("Daily reminder off.");
    } finally {
      setBusy(false);
    }
  };

  const notNow = () => {
    dismissPrompt();
    setMode("hidden");
  };

  if (mode === "hidden" && !row) return null;
  if (mode === "hidden" && row && !pushSupported()) return null;

  if (mode === "inapp") {
    return (
      <div
        className="pointer-events-auto w-full max-w-xs rounded-2xl bg-[#0a0a0c]/[0.85] border border-white/15 backdrop-blur-md px-3 py-2.5 shadow-lg"
        data-testid="inapp-browser-notice"
      >
        <div className="flex items-start gap-2">
          <Bell className="w-3.5 h-3.5 mt-0.5 text-[#ccff00] shrink-0" />
          <div className="text-[12px] text-zinc-100 leading-snug flex-1">
            Open SpotDrop in your browser to enable parking notifications.
          </div>
          <button
            type="button"
            onClick={() => {
              dismissNotice();
              setMode("hidden");
            }}
            className="w-6 h-6 -mr-1 -mt-1 shrink-0 rounded-full flex items-center justify-center text-zinc-500 hover:text-white"
            aria-label="Hide browser notice"
            data-testid="inapp-notice-close"
          >
            <X className="w-3.5 h-3.5" />
          </button>
        </div>
        <button
          type="button"
          onClick={() =>
            openInExternalBrowser((kind, msg) =>
              kind === "success" ? toast.success(msg) : toast.message(msg)
            )
          }
          className="mt-2 h-8 px-3 rounded-full bg-[#ccff00] text-[#0a0a0c] font-semibold text-[12px] inline-flex items-center gap-1.5 active:scale-95 transition-transform"
          data-testid="inapp-open-browser-btn"
        >
          <ExternalLink className="w-3.5 h-3.5" />
          Open in browser
        </button>
      </div>
    );
  }

  if (mode === "prompt") {
    if (row) {
      return (
        <button
          type="button"
          onClick={turnOn}
          disabled={busy}
          className="pointer-events-auto inline-flex items-center gap-1.5 px-3 h-8 rounded-full border border-white/15 bg-[#0a0a0c] text-[12px] text-zinc-100 active:scale-95 transition-transform disabled:opacity-60"
          data-testid="push-toggle-btn"
          aria-pressed={false}
        >
          {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <BellOff className="w-3.5 h-3.5 text-zinc-400" />}
          Off
        </button>
      );
    }
    return (
      <div
        className="pointer-events-auto w-full max-w-xs rounded-2xl bg-[#0a0a0c]/[0.85] border border-white/15 backdrop-blur-md px-3 py-2.5 shadow-lg"
        data-testid="push-prompt-card"
      >
        <div className="flex items-start gap-2">
          <Bell className="w-3.5 h-3.5 mt-0.5 text-[#ccff00] shrink-0" />
          <div className="text-[12px] text-zinc-100 leading-snug flex-1">
            Get one daily reminder to share free parking spots.
          </div>
          <button
            type="button"
            onClick={notNow}
            className="w-6 h-6 -mr-1 -mt-1 shrink-0 rounded-full flex items-center justify-center text-zinc-500 hover:text-white"
            aria-label="Hide notification prompt"
            data-testid="push-prompt-close"
          >
            <X className="w-3.5 h-3.5" />
          </button>
        </div>
        <div className="mt-2 flex items-center gap-2">
          <button
            type="button"
            onClick={turnOn}
            disabled={busy}
            className="h-8 px-3 rounded-full bg-[#ccff00] text-[#0a0a0c] font-semibold text-[12px] inline-flex items-center gap-1.5 active:scale-95 transition-transform disabled:opacity-60"
            data-testid="push-enable-btn"
          >
            {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Bell className="w-3.5 h-3.5" />}
            Enable
          </button>
          <button
            type="button"
            onClick={notNow}
            className="h-8 px-3 rounded-full border border-white/15 text-zinc-300 text-[12px]"
            data-testid="push-notnow-btn"
          >
            Not now
          </button>
        </div>
      </div>
    );
  }

  const on = mode === "on";
  return (
    <button
      type="button"
      onClick={on ? turnOff : turnOn}
      disabled={busy}
      className={`pointer-events-auto inline-flex items-center gap-1.5 px-3 ${row ? "h-8" : "h-9"} rounded-full bg-[#0a0a0c]/70 backdrop-blur-md text-[12px] text-zinc-100 shadow-md active:scale-95 transition-transform border ${
        on ? "border-[#ccff00]/30" : "border-white/15"
      }`}
      data-testid="push-toggle-btn"
      aria-pressed={on}
    >
      {busy ? (
        <Loader2 className="w-3.5 h-3.5 animate-spin" />
      ) : on ? (
        <Bell className="w-3.5 h-3.5 text-[#ccff00]" />
      ) : (
        <BellOff className="w-3.5 h-3.5 text-zinc-400" />
      )}
      {row ? (on ? "On" : "Off") : `Daily reminder ${on ? "on" : "off"}`}
    </button>
  );
}
